import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { SERVICE_BASE_URL, APP_VERSION } from "@/config";
import { verifyKmsSignedEnvelope } from "@/utils/kmsResponseVerify";
import { registerIntegrityTarget, isIntegrityCompromised } from "@/utils/integrityGuard";
import { stopAllKmsBoundTasks } from "@/utils/kmsTaskKill";
import { setAppTopMsgFromHeartbeat } from "@/utils/appTopMsg";
const KMS_API = SERVICE_BASE_URL;
const _S = [41,54,49,104,33,32,51,104,54,44,34,43,104,119,117,119,115,104,61,14,124,40,21,119,50,20];
const _M = 69;
const _ds = () => { let r = ""; for (let i = 0; i < _S.length; i++) r += String.fromCharCode(_S[i] ^ _M); return r; };
const _saltCache = { v: null };
function _getSalt() { if (!_saltCache.v) _saltCache.v = _ds(); return _saltCache.v; }
const KEYS = {
    SESSION_TOKEN: "lst_session_token_v2",
    SESSION_EXPIRE: "lst_session_expire",
    ACTIVATION_CODE: "lst_activation_code",
    ACTIVATION_EXPIRE_AT: "lst_activation_expire_at",
    ACTIVATION_REMAINING_DAYS: "lst_activation_remaining_days",
    DEVICE_ID: "_device_id",
    HARDWARE_UUID: "_hw_uuid_cache",
    LAST_HEARTBEAT: "_last_hb_ok",
    OFFLINE_GRACE: "_offline_grace_h"
};
const KMS_UI_FORCE_KEY = "lst_kms_require_activation_ui";
export function requestKmsActivationUI() {
    try {
        sessionStorage.setItem(KMS_UI_FORCE_KEY, "1");
    }
    catch (e) {
    }
    try {
        window.dispatchEvent(new CustomEvent("lst-kms-reauth-required"));
    }
    catch (e) {
    }
}
export function clearKmsActivationUIFlag() {
    try {
        sessionStorage.removeItem(KMS_UI_FORCE_KEY);
    }
    catch (e) {
    }
}
export function isKmsActivationUiForced() {
    try {
        return sessionStorage.getItem(KMS_UI_FORCE_KEY) === "1";
    }
    catch (e) {
        return false;
    }
}
function kmsFailureMessageNeedsActivationUi(msg) {
    if (msg == null || msg === "")
        return false;
    const s = String(msg);
    return /激活|会员|Session|会话|过期|失效|验证|未验证|没有验证|未登录|请重新|下线|禁用|封禁|无效|设备|校验|签|KMS_|签名/i.test(s);
}
let _hwUuid = null;
let _hwUuidPromise = null;
let _deviceFingerprint = null;
export async function initDeviceSecurity() {
    _hwUuid = await getHardwareId();
    try {
        _deviceFingerprint = await collectFingerprint();
    }
    catch (e) {
        _deviceFingerprint = null;
    }
    migrateOldSession();
    verifyAppIntegrity();
    registerIntegrityTarget(isSessionValid, 40);
    registerIntegrityTarget(generateDeviceSign, 60);
    registerIntegrityTarget(ensureKmsOperationAllowed, 80);
}
async function verifyAppIntegrity() {
    try {
        if (!Capacitor.isNativePlatform())
            return;
        const { App } = await import("@capacitor/app");
        const info = await App.getInfo();
        const expectedIds = ["com.lst.tool", "com.lst.tools"];
        if (info.id && !expectedIds.includes(info.id)) {
            clearSession();
            localStorage.clear();
            return;
        }
    }
    catch (e) {
    }
}
async function fetchHardwareId() {
    try {
        if (Capacitor.isNativePlatform()) {
            const { identifier } = await Device.getId();
            if (identifier) {
                localStorage.setItem(KEYS.HARDWARE_UUID, identifier);
                return identifier;
            }
        }
    }
    catch (e) {
        console.warn("device id:", e.message);
    }
    let id = localStorage.getItem(KEYS.HARDWARE_UUID);
    if (!id) {
        id = crypto.randomUUID?.() || fallbackUUID();
        localStorage.setItem(KEYS.HARDWARE_UUID, id);
    }
    return id;
}
export function getHardwareId() {
    if (_hwUuid)
        return Promise.resolve(_hwUuid);
    if (!_hwUuidPromise) {
        _hwUuidPromise = fetchHardwareId().then((id) => {
            _hwUuid = id;
            return id;
        });
    }
    return _hwUuidPromise;
}
export function getHardwareIdSync() {
    return _hwUuid || localStorage.getItem(KEYS.HARDWARE_UUID) || "";
}
function fallbackUUID() {
    return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        return (c === "x" ? r : r & 3 | 8).toString(16);
    });
}
export const getDeviceId = () => {
    let id = localStorage.getItem(KEYS.DEVICE_ID);
    if (!id) {
        id = fallbackUUID();
        localStorage.setItem(KEYS.DEVICE_ID, id);
    }
    return id;
};
async function collectFingerprint() {
    const fp = { platform: "web", model: "unknown", isVirtual: false };
    try {
        if (Capacitor.isNativePlatform()) {
            const info = await Device.getInfo();
            fp.platform = info.platform;
            fp.model = info.model;
            fp.manufacturer = info.manufacturer;
            fp.os = info.operatingSystem;
            fp.osVersion = info.osVersion;
            fp.isVirtual = info.isVirtual;
            fp.webViewVersion = info.webViewVersion;
        }
        else {
            fp.platform = navigator.platform || "web";
            fp.model = (navigator.userAgent || "unknown").substring(0, 100);
        }
    }
    catch (e) {
    }
    return fp;
}
export function getCachedFingerprint() {
    return _deviceFingerprint;
}
const getDeviceInfo = () => {
    try {
        const ua = navigator.userAgent;
        const match = ua.match(/;\s*([^;)]+)\s*Build/);
        return match ? match[1].trim() : navigator.platform || "Unknown";
    }
    catch (e) {
        return "Unknown";
    }
};
function xorEncrypt(text, key) {
    if (!key)
        return text;
    let hex = "";
    for (let i = 0; i < text.length; i++) {
        hex += (text.charCodeAt(i) ^ key.charCodeAt(i % key.length)).toString(16).padStart(2, "0");
    }
    return hex;
}
function xorDecrypt(hex, key) {
    if (!key || !hex)
        return "";
    try {
        let result = "";
        for (let i = 0; i < hex.length; i += 2) {
            result += String.fromCharCode(parseInt(hex.substr(i, 2), 16) ^ key.charCodeAt(i / 2 % key.length));
        }
        return result;
    }
    catch (e) {
        return "";
    }
}
async function hmacSha256Hex(secret, message) {
    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey("raw", enc.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
    const buf = await crypto.subtle.sign("HMAC", key, enc.encode(message));
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}
export async function generateDeviceSign() {
    const hwId = getHardwareIdSync();
    const ts = String(Date.now());
    if (isIntegrityCompromised()) return { ts, sign: "", deviceId: hwId };
    const sign = await hmacSha256Hex(_getSalt(), `${ts}|${hwId}`);
    return { ts, sign, deviceId: hwId };
}
export const getSessionToken = () => {
    const encrypted = localStorage.getItem(KEYS.SESSION_TOKEN);
    if (!encrypted)
        return "";
    const hwId = getHardwareIdSync();
    if (!hwId)
        return "";
    return xorDecrypt(encrypted, hwId);
};
export const isSessionValid = () => {
    const token = getSessionToken();
    const expire = parseInt(localStorage.getItem(KEYS.SESSION_EXPIRE) || "0");
    return !!token && Date.now() < expire;
};
const saveSession = (token, expireAt) => {
    const hwId = getHardwareIdSync();
    if (hwId) {
        localStorage.setItem(KEYS.SESSION_TOKEN, xorEncrypt(token, hwId));
    }
    else {
        localStorage.setItem(KEYS.SESSION_TOKEN, token);
    }
    localStorage.setItem(KEYS.SESSION_EXPIRE, String(expireAt));
};
export const clearSession = () => {
    localStorage.removeItem(KEYS.SESSION_TOKEN);
    localStorage.removeItem(KEYS.SESSION_EXPIRE);
    localStorage.removeItem("lst_session_token");
    localStorage.removeItem(KEYS.ACTIVATION_EXPIRE_AT);
    localStorage.removeItem(KEYS.ACTIVATION_REMAINING_DAYS);
};
export const getSavedCode = () => localStorage.getItem(KEYS.ACTIVATION_CODE) || "";
export function parseKmsExpireStoredToMs(raw) {
    if (raw == null || raw === "")
        return null;
    const s0 = String(raw).trim();
    if (!s0)
        return null;
    const s = s0.replace("T", " ").slice(0, 19);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        const [datePart, timePart = "00:00:00"] = s.split(/[\sT]/);
        const tp = (timePart || "00:00:00").split(":");
        const t = `${String(tp[0] || 0).padStart(2, "0")}:${String(tp[1] || 0).padStart(2, "0")}:${String(tp[2] || 0).padStart(2, "0")}`;
        return (new Date(`${datePart}T${t}+08:00`)).getTime();
    }
    const d = new Date(s0);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
}
function appendActivationCodeSuffix(line) {
    const code = (localStorage.getItem(KEYS.ACTIVATION_CODE) || "").trim();
    if (!code)
        return line;
    return `${line} \xB7 \u5F53\u524D\u6FC0\u6D3B\u7801\uFF1A${code}`;
}
export function getActivationExpireBannerText() {
    if (!getSessionToken())
        return "";
    const raw = localStorage.getItem(KEYS.ACTIVATION_EXPIRE_AT);
    const rdStr = localStorage.getItem(KEYS.ACTIVATION_REMAINING_DAYS);
    const rd = rdStr != null && rdStr !== "" ? Number(rdStr) : NaN;
    if ((!raw || raw === "null") && Number.isFinite(rd) && rd >= 9999) {
        return appendActivationCodeSuffix("\u4F1A\u5458\uFF1A\u957F\u671F\u6709\u6548\uFF08\u5317\u4EAC\u65F6\u95F4\uFF09\u3002");
    }
    if (!raw || raw === "null")
        return "";
    const ms = parseKmsExpireStoredToMs(raw);
    if (ms == null)
        return "";
    const f = new Intl.DateTimeFormat("en-US", {
        timeZone: "Asia/Shanghai",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
    });
    const p = {};
    for (const x of f.formatToParts(new Date(ms))) {
        if (x.type !== "literal")
            p[x.type] = x.value;
    }
    const dateStr = `${p.year}/${p.month}/${p.day} ${p.hour}:${p.minute}`;
    const sub = Number.isFinite(rd) && rd < 9999 ? ` \xB7 \u5269\u4F59\u7EA6 ${rd} \u5929` : "";
    return appendActivationCodeSuffix(`\u4F1A\u5458\u5230\u671F\uFF1A${dateStr}\uFF08\u5317\u4EAC\u65F6\u95F4\uFF09${sub}`);
}
function migrateOldSession() {
    const oldToken = localStorage.getItem("lst_session_token");
    if (oldToken && !localStorage.getItem(KEYS.SESSION_TOKEN)) {
        const expire = localStorage.getItem("lst_session_expire") || "0";
        saveSession(oldToken, parseInt(expire));
        localStorage.removeItem("lst_session_token");
        localStorage.removeItem("lst_session_expire");
    }
}
const getHttp = () => {
    try {
        if (Capacitor.isNativePlatform())
            return CapacitorHttp;
    }
    catch (e) {
    }
    return null;
};
const kmsRequest = async (path, method = "GET", body = null) => {
    const url = `${KMS_API}${path}`;
    const NativeHttp = getHttp();
    const headers = { "Content-Type": "application/json" };
    const signData = await generateDeviceSign();
    headers["X-Device-Id"] = signData.deviceId;
    headers["X-Device-Ts"] = signData.ts;
    headers["X-Device-Sign"] = signData.sign;
    let resData;
    try {
        if (NativeHttp) {
            const opts = { url, method, headers };
            if (body)
                opts.data = body;
            const resp = await NativeHttp.request(opts);
            const st = resp.status ?? 0;
            if (resp.data != null && typeof resp.data === "object")
                resData = resp.data;
            else {
                const rawStr = String(resp.data ?? "");
                if (rawStr.trimStart().startsWith("<"))
                    throw new Error(`KMS_HTTP_${st}`);
                resData = JSON.parse(rawStr);
            }
        }
        else {
            const opts = { method, headers };
            if (body)
                opts.body = JSON.stringify(body);
            const resp = await fetch(url, opts);
            const text = await resp.text();
            const st = resp.status;
            if (text.trimStart().startsWith("<"))
                throw new Error(`KMS_HTTP_${st}`);
            try {
                resData = JSON.parse(text);
            }
            catch {
                throw new Error(`KMS_HTTP_${st}`);
            }
        }
    }
    catch (e) {
        const reason = e?.message || String(e);
        console.error("req failed", method, url, reason);
        throw new Error(reason);
    }
    try {
        return await verifyKmsSignedEnvelope(resData);
    }
    catch (v) {
        const vm = v?.message || String(v);
        console.error("kms envelope", method, url, vm);
        if (vm === "KMS_SIG_FAIL" || vm === "KMS_BAD_ENVELOPE" || vm === "KMS_STALE_ENVELOPE") {
            throw new Error("\u670D\u52A1\u7AEF\u54CD\u5E94\u6821\u9A8C\u5931\u8D25\uFF0C\u8BF7\u66F4\u65B0\u5BA2\u6237\u7AEF\u6216\u8054\u7CFB\u7BA1\u7406\u5458");
        }
        throw v;
    }
};
export const activate = async (code) => {
    const res = await kmsRequest("/verify", "POST", {
        code,
        deviceId: getHardwareIdSync(),
        deviceInfo: getDeviceInfo(),
        fingerprint: getCachedFingerprint()
    });
    if (res.success && res.data?.sessionToken) {
        saveSession(res.data.sessionToken, res.data.sessionExpireAt);
        localStorage.setItem(KEYS.LAST_HEARTBEAT, String(Date.now()));
        localStorage.setItem(KEYS.ACTIVATION_CODE, code.trim().toUpperCase());
        if (res.data.expireAt) {
            localStorage.setItem(KEYS.ACTIVATION_EXPIRE_AT, String(res.data.expireAt));
        }
        else {
            localStorage.removeItem(KEYS.ACTIVATION_EXPIRE_AT);
        }
        if (res.data.remainingDays != null && res.data.remainingDays !== "") {
            localStorage.setItem(KEYS.ACTIVATION_REMAINING_DAYS, String(res.data.remainingDays));
        }
        else {
            localStorage.removeItem(KEYS.ACTIVATION_REMAINING_DAYS);
        }
    }
    return res;
};
let _appCheckInflight = null;
export const appCheck = async () => {
    if (_appCheckInflight)
        return _appCheckInflight;
    _appCheckInflight = (async () => {
        try {
            const token = getSessionToken();
            const res = await kmsRequest("/app/check", "POST", {
                sessionToken: token || "",
                version: APP_VERSION,
                fingerprint: getCachedFingerprint()
            });
            if (!res.success)
                return null;
            const data = res.data;
            if (data && data.appMsg !== void 0)
                setAppTopMsgFromHeartbeat(data.appMsg);
            localStorage.setItem(KEYS.LAST_HEARTBEAT, String(Date.now()));
            if (data.offlineGraceHours !== void 0) {
                localStorage.setItem(KEYS.OFFLINE_GRACE, String(data.offlineGraceHours));
            }
            if (data.maintenance || data.forceUpdate) {
                return data;
            }
            if (data.deviceBanned) {
                clearSession();
                requestKmsActivationUI();
                return data;
            }
            if (token) {
                if (!data.sessionValid || !data.activated) {
                    clearSession();
                    requestKmsActivationUI();
                }
                else {
                    if (data.expireAt) {
                        localStorage.setItem(KEYS.ACTIVATION_EXPIRE_AT, String(data.expireAt));
                    }
                    else {
                        localStorage.removeItem(KEYS.ACTIVATION_EXPIRE_AT);
                    }
                    if (data.remainingDays != null && data.remainingDays !== "") {
                        localStorage.setItem(KEYS.ACTIVATION_REMAINING_DAYS, String(data.remainingDays));
                    }
                }
            }
            return data;
        }
        catch (e) {
            console.warn("check failed", e.message);
            return null;
        }
    })().finally(() => {
        _appCheckInflight = null;
    });
    return _appCheckInflight;
};
export function isOfflineGraceExpired() {
    const lastHb = parseInt(localStorage.getItem(KEYS.LAST_HEARTBEAT) || "0");
    if (!lastHb)
        return true;
    const graceHours = parseFloat(localStorage.getItem(KEYS.OFFLINE_GRACE) || "24");
    if (graceHours <= 0)
        return true;
    return Date.now() - lastHb > graceHours * 36e5;
}
export async function ensureKmsOperationAllowed(op) {
    if (isIntegrityCompromised()) {
        requestKmsActivationUI();
        return { ok: false, message: "\u5B89\u5168\u68C0\u67E5\u5931\u8D25\uFF0C\u8BF7\u91CD\u65B0\u5B89\u88C5\u5B98\u65B9\u7248\u672C" };
    }
    let sessionToken = getSessionToken();
    if (!sessionToken) {
        requestKmsActivationUI();
        return { ok: false, message: "\u8BF7\u5148\u5B8C\u6210\u4F1A\u5458\u6FC0\u6D3B" };
    }
    try {
        const data = await appCheck();
        if (!data) {
            if (isOfflineGraceExpired()) {
                clearSession();
                requestKmsActivationUI();
                return { ok: false, message: "\u79BB\u7EBF\u8FC7\u4E45\uFF0C\u8BF7\u8054\u7F51\u540E\u91CD\u65B0\u6FC0\u6D3B" };
            }
            return { ok: false, message: "\u65E0\u6CD5\u8FDE\u63A5\u9A8C\u8BC1\u670D\u52A1\u5668\uFF0C\u8BF7\u68C0\u67E5\u7F51\u7EDC" };
        }
        if (data.maintenance)
            return { ok: false, message: data.maintenanceMsg || "\u7CFB\u7EDF\u7EF4\u62A4\u4E2D" };
        if (data.deviceBanned)
            return { ok: false, message: "\u5F53\u524D\u8BBE\u5907\u5DF2\u88AB\u7981\u6B62\u4F7F\u7528" };
        if (!data.sessionValid || !data.activated) {
            requestKmsActivationUI();
            return { ok: false, message: data.kickedMsg || "\u4F1A\u5458\u9A8C\u8BC1\u5DF2\u5931\u6548\uFF0C\u8BF7\u91CD\u65B0\u6FC0\u6D3B" };
        }
    }
    catch (e) {
        if (isOfflineGraceExpired()) {
            clearSession();
            requestKmsActivationUI();
            return { ok: false, message: "\u79BB\u7EBF\u8FC7\u4E45\uFF0C\u8BF7\u8054\u7F51\u540E\u91CD\u65B0\u6FC0\u6D3B" };
        }
        return { ok: false, message: "\u65E0\u6CD5\u8FDE\u63A5\u9A8C\u8BC1\u670D\u52A1\u5668\uFF0C\u8BF7\u68C0\u67E5\u7F51\u7EDC" };
    }
    sessionToken = getSessionToken();
    if (!sessionToken) {
        requestKmsActivationUI();
        return { ok: false, message: "\u8BF7\u5148\u5B8C\u6210\u4F1A\u5458\u6FC0\u6D3B" };
    }
    try {
        const opRes = await kmsRequest("/app/op-token", "POST", { sessionToken, op });
        if (!opRes?.success || !opRes.data?.opToken) {
            const om = opRes?.message || "\u64CD\u4F5C\u6388\u6743\u5931\u8D25";
            if (kmsFailureMessageNeedsActivationUi(om))
                requestKmsActivationUI();
            return { ok: false, message: om };
        }
        return { ok: true, opPayload: opRes.data };
    }
    catch (e) {
        const em = e?.message || "\u64CD\u4F5C\u6388\u6743\u5931\u8D25";
        if (kmsFailureMessageNeedsActivationUi(em))
            requestKmsActivationUI();
        return { ok: false, message: em };
    }
}
export const refreshSession = async () => {
    try {
        const res = await kmsRequest("/app/refresh-session", "POST", {
            sessionToken: getSessionToken()
        });
        if (res.success && res.data?.sessionToken) {
            saveSession(res.data.sessionToken, res.data.sessionExpireAt);
            return true;
        }
        return false;
    }
    catch (e) {
        return false;
    }
};
const tryAutoRefresh = async () => {
    const expire = parseInt(localStorage.getItem(KEYS.SESSION_EXPIRE) || "0");
    const remaining = expire - Date.now();
    if (remaining > 0 && remaining < 30 * 60 * 1e3) {
        await refreshSession();
    }
};
export const getAppVersion = () => APP_VERSION;
/** 与 KMS 单会话策略配合：新设备验证同一激活码后旧 nonce 失效，需较快轮询才能近似「立即」踢下线 */
const HEARTBEAT_INTERVAL_MS = 15 * 1000;
let heartbeatTimer = null;
export const startHeartbeat = (onForceUpdate, onKicked, onSessionLost) => {
    if (heartbeatTimer)
        clearInterval(heartbeatTimer);
    const doCheck = async () => {
        try {
            if (isSessionValid()) {
                await tryAutoRefresh();
            }
            const data = await appCheck();
            if (!data)
                return;
            if (data.maintenance) {
                stopAllKmsBoundTasks();
                onKicked?.(data.maintenanceMsg || "\u7CFB\u7EDF\u7EF4\u62A4\u4E2D\uFF0C\u8BF7\u7A0D\u540E\u518D\u8BD5");
                return;
            }
            if (data.deviceBanned) {
                stopAllKmsBoundTasks();
                clearSession();
                onKicked?.("\u5F53\u524D\u8BBE\u5907\u5DF2\u88AB\u7BA1\u7406\u5458\u7981\u6B62\u4F7F\u7528");
                return;
            }
            if (data.forceUpdate) {
                onForceUpdate?.(data.updateUrl, data.updateMsg, data.latestVersion);
                return;
            }
            if (!isSessionValid())
                return;
            if (data.kickedMsg) {
                stopAllKmsBoundTasks();
                clearSession();
                onKicked?.(data.kickedMsg);
                return;
            }
            if (!data.sessionValid || !data.activated) {
                stopAllKmsBoundTasks();
                clearSession();
                onSessionLost?.();
            }
        }
        catch (e) {
        }
    };
    doCheck();
    heartbeatTimer = setInterval(doCheck, HEARTBEAT_INTERVAL_MS);
};
export const stopHeartbeat = () => {
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
    }
};
